import {
	noop,
	NotificationService,
	type StepperRef,
	useIntl,
	type UserMobileVerificationRequirementConfig,
	type UserRequirementStateStatus,
	useSetPageTitle
} from '@capsicum/common';
import { Alert, Snackbar, Stepper, useStepperConfirmationModal } from '@capsicum/common-web';
import { InfoIcon } from '@capsicum/common-web/icons';
import { type Requirement } from '@capsicum/socios-core';
import { GeeTestContainer, type GeeTestRef, useTriggerCaptcha } from '@capsicum/socios-core-web';
import { useMobileVerification } from 'libs/features/regulations/shared/src/hooks';
import { useRef } from 'react';
import styled from 'styled-components';

import { CodeVerification } from '../code-verification/CodeVerification';

import { MobileInput } from './MobileInput';

type MobileVerificationProps = {
	isForcedRequirement?: boolean;
	isUniqueRequirement?: boolean;
	onDismiss: () => void;
	requirement: Requirement<UserMobileVerificationRequirementConfig>;
	status?: UserRequirementStateStatus;
};

export const MobileVerification = ({
	isForcedRequirement,
	isUniqueRequirement,
	onDismiss,
	requirement,
	status
}: MobileVerificationProps) => {
	const { $t } = useIntl();
	const stepperRef = useRef<StepperRef>(null);
	const captchaRef = useRef<GeeTestRef>(null);
	const startCaptcha = useTriggerCaptcha({ captcha: captchaRef.current });
	const { openExitConfirmationModal } = useStepperConfirmationModal();

	const triggerCaptcha = async () => {
		const response = await startCaptcha();

		setCaptchaInfo({
			captcha_id: response.captchaId,
			captcha_output: response.captchaOutput,
			gen_time: response.genTime,
			lot_number: response.lotNumber,
			pass_token: response.passToken
		});
	};

	const onCodeVerificationSuccess = () => {
		NotificationService.addNotification({
			component: (
				<Snackbar
					description={$t('jurisdictionRequirements.mobileVerification.successMessage')}
					testID="mobile-verification-success-notification"
					variant="success"
				/>
			)
		});

		if (isUniqueRequirement) onDismiss();
	};

	const {
		isFirstButtonLoading,
		isReadonlyMode,
		isUserOnCodeInputStep,
		mobileCodeVerificationError,
		mobileNumberForm,
		mobileNumberWithPrefix,
		onChangeTargetClicked,
		onCodeSubmit,
		onNextStep,
		onSecondaryButtonClick,
		setCaptchaInfo,
		setCurrentStep
	} = useMobileVerification(requirement, {
		onCodeVerificationSuccess,
		onDismiss,
		openExitConfirmationModal,
		status,
		stepperRef,
		triggerCaptcha
	});

	useSetPageTitle($t('jurisdictionRequirements.mobileVerification.header'));

	const MobileInputStep = () => <MobileInput form={mobileNumberForm} readonly={isReadonlyMode} />;
	const CodeVerificationStep = () => (
		<CodeVerification
			error={mobileCodeVerificationError}
			stepperRef={stepperRef}
			target={mobileNumberWithPrefix}
			texts={{
				resendButtonText: $t('auth.sendBySms'),
				timerMessage: 'auth.welcome.codeInput.timerMessageWithSMS'
			}}
			onChangeTargetClicked={onChangeTargetClicked}
			onCodeSubmit={onCodeSubmit}
			onResendPress={() => stepperRef.current?.goToPrevStep()}
		/>
	);

	return (
		<S.Wrapper>
			<Stepper
				disabledFirstButton={!mobileNumberForm.formState.isValid}
				firstButtonText={isReadonlyMode ? $t('global.button.back') : $t('global.button.verify')}
				fullscreen={false}
				hiddenFirstButton={isUserOnCodeInputStep}
				hiddenSecondButton={isReadonlyMode || isForcedRequirement}
				hideActionButtons={isUserOnCodeInputStep}
				isLoading={isFirstButtonLoading}
				prerenderSteps={false}
				ref={stepperRef}
				steps={[MobileInputStep, CodeVerificationStep]}
				testID="mobile-verification-stepper"
				onFirstButtonClick={onNextStep}
				onSecondButtonClick={onSecondaryButtonClick}
				onStepIndexChange={setCurrentStep}
				onSubmitSteps={noop}
			/>
			{isUserOnCodeInputStep && (
				<S.AlertContainer>
					<Alert
						closable={false}
						icon={InfoIcon}
						subTitle={$t('auth.welcome.codeInput.info')}
						testID="email-verification-code-info-alert"
						type="message"
					/>
				</S.AlertContainer>
			)}
			<GeeTestContainer ref={captchaRef} />
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: ${({ theme }) => `${theme.spacings.medium} 0 0`};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small} 0`};
		}
	`;

	export const AlertContainer = styled.div`
		margin-top: ${({ theme }) => theme.sizing.medium};
	`;
}
